/**
 * Avatar con las iniciales de una persona. Es decorativo (`aria-hidden`): el nombre completo va escrito al lado.
 */
import './Avatar.css';

const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'san', 'santa', 'van', 'von']);

/**
 * Iniciales para el avatar: nombre + primer apellido. En nombres chilenos (1-3 nombres y 2 apellidos)
 * el primer apellido es la penúltima palabra: «Stephen Yerko Antuan Lucic Maldonado» da «SL».
 * Con 2 palabras: primera + última; con 1: su inicial. Se ignoran partículas («de», «del», «la»…).
 */
export function iniciales(nombre: string): string {
  const letras = nombreYApellido(nombre)
    .map((p) => Array.from(p)[0] ?? '')
    .join('');
  return letras.toUpperCase() || '?';
}

/**
 * Nombre corto para lugares estrechos (tarjetas, lista, historial): primer nombre + primer apellido,
 * con la misma regla que `iniciales`. «Stephen Yerko Antuan Lucic Maldonado» da «Stephen Lucic».
 * Quien lo use pone el nombre completo en `title`.
 */
export function nombreCorto(nombre: string): string {
  return nombreYApellido(nombre).join(' ') || nombre.trim();
}

/** Las (hasta) dos palabras que representan a la persona: nombre y primer apellido. */
function nombreYApellido(nombre: string): string[] {
  const todas = nombre.trim().split(/\s+/).filter(Boolean);
  const palabras = todas.filter((p) => !PARTICULAS.has(p.toLowerCase()));
  const base = palabras.length > 0 ? palabras : todas;
  const elegidas = base.length >= 3 ? [base[0], base[base.length - 2]] : [base[0], base[1]];
  return elegidas.filter((p): p is string => Boolean(p));
}

export function Avatar({ nombre, grande = false }: { nombre: string; grande?: boolean }) {
  return (
    <span
      className={grande ? 'avatar-chico avatar-chico--grande' : 'avatar-chico'}
      aria-hidden="true"
    >
      {iniciales(nombre)}
    </span>
  );
}
